import React, { useEffect, useState } from 'react';
import './ResultsDisplay.css';
import BetterOptions from './BetterOptions';
import EmailCapture from './EmailCapture';
import { trackResultViewed, trackShareClicked } from '../utils/analytics';

function ResultsDisplay({ result, onNewEvaluation, onProvideFeedback, onEmailCapture }) {
  const getScoreColor = (score) => {
    if (score >= 80) return '#4caf50'; // Green
    if (score >= 60) return '#ff9800'; // Orange
    if (score >= 40) return '#ff6f00'; // Deep Orange
    return '#d32f2f'; // Red
  };

  const getVerdictClass = (verdict) => {
    if (verdict === 'Comfortable') return 'verdict-comfortable';
    if (verdict === 'Stretch') return 'verdict-stretch';
    if (verdict === 'Risky') return 'verdict-risky';
    return 'verdict-not-advised';
  };

  const formatCurrency = (value) => {
    const num = Number(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  };

  const safeFixed = (value, digits = 1) => {
    const n = Number(value);
    if (!isFinite(n)) return (0).toFixed(digits);
    return n.toFixed(digits);
  };

  const formatPercent = (value) => {
    const n = Number(value);
    if (!isFinite(n)) return '0.0%';
    return `${n.toFixed(1)}%`;
  };

  const metrics = result.metrics || {};
  const comparison = result.market_rate_comparison || {};

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    trackResultViewed({ verdict: result.verdict, score: result.affordability_score });
  }, [result.verdict, result.affordability_score]);

  const handleShare = () => {
    const text = `My affordability score: ${result.affordability_score}/100 (${result.verdict})\nFind out if you can afford your next purchase:\nhttps://affordabilityanalyzer.com`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    trackShareClicked({ verdict: result.verdict, score: result.affordability_score });
  };

  const monthsSaved = metrics.months_of_expenses_saved != null
    ? Number(metrics.months_of_expenses_saved)
    : (metrics.months_of_expenses_after != null ? Number(metrics.months_of_expenses_after) : 0);
  const paymentPercent = Number(metrics.payment_percent_disposable) || 0;
  const emergencyTarget = Number(metrics.emergency_fund_target_months) || 6;
  const upcomingMajor = Number(metrics.major_expenses_coming) || 0;

  return (
    <div className="results-display">
      {/* Score Card */}
      <div className="card score-card">
        <div className="score-circle" style={{ borderColor: getScoreColor(result.affordability_score) }}>
          <div className="score-number">{result.affordability_score}</div>
          <div className="score-label">Score</div>
        </div>

        <div className={`verdict-badge ${getVerdictClass(result.verdict)}`}>
          {result.verdict}
        </div>

        <p className="score-description">
          {result.verdict === 'Comfortable' && '✅ This purchase fits your budget well'}
          {result.verdict === 'Stretch' && '⚠️ Feasible but requires discipline'}
          {result.verdict === 'Risky' && (<>⛔ This purchase creates significant financial strain. <a href="#better-options">See what you can change →</a></>)}
          {result.verdict === 'Not advised' && (<>🚫 This purchase may put you at financial risk. <a href="#better-options">See what you can change →</a></>)}
        </p>
      </div>

      {/* Recommendation (show when not Comfortable) */}
      {result.recommendation && result.verdict !== 'Comfortable' && (
        <div className="card recommendation-card">
          <h3>💭 Recommended Purchase Price</h3>
          <div className="recommendation-grid">
            <div className="recommendation-item">
              <label>Cash maximum</label>
              <div className="recommendation-value">{formatCurrency(result.recommendation.recommended_cash_max)}</div>
              <div className="recommendation-note">{result.recommendation.rationale?.cash_rule}</div>
            </div>

            <div className="recommendation-item">
              <label>Financed maximum</label>
              <div className="recommendation-value">{formatCurrency(result.recommendation.recommended_financed_max)}</div>
              <div className="recommendation-note">{result.recommendation.rationale?.finance_rule}</div>
            </div>
          </div>

          {Array.isArray(result.recommendation.notes) && result.recommendation.notes.length > 0 && (
            <ul className="recommendation-notes">
              {result.recommendation.notes.map((n, idx) => (
                <li key={idx}>{n}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Explanation */}
      <div className="card explanation-card">
        <h3>📝 Assessment</h3>
        <div 
          className="explanation-text"
          dangerouslySetInnerHTML={{ __html: result.explanation }}
        />
      </div>

      {/* Financial Metrics */}
      <div className="card metrics-card">
        <h3>💡 Financial Metrics</h3>

        <div className="metrics-grid">
          <div className="metric">
            <label>Monthly Income</label>
            <div className="metric-value">{formatCurrency(metrics.monthly_income)}</div>
          </div>

          <div className="metric">
            <label>Fixed Expenses</label>
            <div className="metric-value">{formatCurrency(metrics.monthly_fixed_expenses)}</div>
          </div>

          <div className="metric">
            <label>Debt Payments</label>
            <div className="metric-value">{formatCurrency(metrics.monthly_debt_payments)}</div>
          </div>

          <div className="metric">
            <label>Disposable Income</label>
            <div className="metric-value highlight">{formatCurrency(metrics.disposable_income)}</div>
          </div>

          <div className="metric">
            <label>Purchase Price</label>
            <div className="metric-value">{formatCurrency(metrics.purchase_price)}</div>
          </div>

          <div className="metric">
            <label>Down Payment</label>
            <div className="metric-value">{formatCurrency(metrics.down_payment)}</div>
          </div>

          <div className="metric">
            <label>Monthly Payment</label>
            <div className="metric-value highlight">{formatCurrency(metrics.monthly_payment)}</div>
          </div>

          <div className="metric">
            <label>% of Disposable Income</label>
            <div className="metric-value">{formatPercent(metrics.payment_percent_disposable)}</div>
          </div>

          <div className="metric">
            <label>Total Interest Cost</label>
            <div className="metric-value">{formatCurrency(metrics.total_interest)}</div>
          </div>

          <div className="metric">
            <label>Savings After Down Payment</label>
            <div className="metric-value highlight">{formatCurrency(metrics.savings_remaining)}</div>
          </div>

          <div className="metric">
            <label>Months of Expenses Saved</label>
            <div className="metric-value">{safeFixed(monthsSaved, 1)} months</div>
          </div>

          <div className="metric">
            <label>Price to Income Ratio</label>
            <div className="metric-value">{formatPercent(metrics.price_to_income_ratio)}</div>
          </div>
        </div>
      </div>

      {/* Key takeaways */}
      <div className="card takeaways-card">
        <h3>🧾 Key Takeaways</h3>
        <ul>
          {metrics.savings_remaining != null && Number(metrics.savings_remaining) < 0 && (
            <li><strong>Warning:</strong> You don't have enough savings to cover the down payment.</li>
          )}
          {monthsSaved < emergencyTarget && (
            <li><strong>Emergency fund low:</strong> After this purchase you'd have {safeFixed(monthsSaved,1)} months saved (target: {emergencyTarget} months).</li>
          )}
          {paymentPercent > 20 && (
            <li><strong>Monthly payment impact:</strong> Payment equals {paymentPercent}% of disposable income — this reduces monthly flexibility.</li>
          )}
          {upcomingMajor > 0 && (
            <li><strong>Upcoming expenses:</strong> You've reported ${upcomingMajor.toLocaleString()} in major expenses over the next 12 months.</li>
          )}
          {!metrics.savings_remaining && paymentPercent <= 20 && monthsSaved >= emergencyTarget && upcomingMajor === 0 && (
            <li>No major immediate concerns detected — continue monitoring your budget.</li>
          )}
        </ul>
      </div>

      
      <BetterOptions verdict={result.verdict} score={result.affordability_score} />

      <EmailCapture
        score={result.affordability_score}
        verdict={result.verdict}
        onSubmit={onEmailCapture}
      />

      {/* Market Rate Comparison */}
      {comparison.user_apr && (
        // show only when comparison has numeric APRs
        <div className="card market-card">
          <h3>📊 Market Rate Comparison</h3>

          <div className="comparison-grid">
            <div className="comparison-item">
              <label>Your APR</label>
              <div className="comparison-value">{comparison.user_apr.toFixed(2)}%</div>
            </div>

            <div className="comparison-item">
              <label>Market Average</label>
              <div className="comparison-value">{comparison.market_avg_apr.toFixed(2)}%</div>
            </div>

            <div className="comparison-item">
              <label>Base Rate</label>
              <div className="comparison-value">{comparison.base_rate.toFixed(2)}%</div>
            </div>

            <div className="comparison-item">
              <label>Spread</label>
              <div className="comparison-value">{comparison.spread}%</div>
            </div>

            <div className="comparison-item full-width">
              <label>Assessment</label>
              <div className={`comparison-badge ${comparison.comparison.toLowerCase().replace('/', '-')}`}>
                {comparison.comparison}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="card actions-card">
        <div className="actions-grid">
          <button className="btn-primary" onClick={onProvideFeedback}>
            👍 Provide Feedback
          </button>
          <button className="btn-secondary" onClick={onNewEvaluation}>
            🔄 New Evaluation
          </button>
          <button className="btn-secondary" onClick={handleShare}>
            {copied ? '✓ Copied!' : '🔗 Share Result'}
          </button>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="disclaimer">
        <p>
          <strong>Disclaimer:</strong> This assessment is educational only and not financial advice.
          Market rates are benchmarks as of the evaluation date. Consult a qualified financial advisor
          for personalized guidance.
        </p>
      </div>
    </div>
  );
}

export default ResultsDisplay;
